import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Phone, MapPin, Car, Calendar, Clock, FileText } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, BRAND } from "../App";

const GetQuotePage = () => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    pickup_address: "", delivery_address: "",
    pickup_date: "", pickup_time: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_type: "",
    runs_drives: true, rolls_steers_brakes: true,
    special_requirements: "", additional_notes: "",
    service_type: "transport",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const nextStep = () => {
    if (step === 1 && (!form.full_name || !form.phone || !form.email)) {
      toast.error("Please fill in all contact information");
      return;
    }
    if (step === 2 && (!form.pickup_address || !form.delivery_address)) {
      toast.error("Please provide pickup and delivery addresses");
      return;
    }
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_make || !form.vehicle_model) {
      toast.error("Please fill in vehicle details");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/transport`, form);
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-20">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl text-center">
            <div className="w-20 h-20 bg-[#E31E24] mx-auto mb-6 flex items-center justify-center">
              <CheckCircle size={48} className="text-white" />
            </div>
            <h1 className="text-4xl font-black uppercase mb-4">Request Received!</h1>
            <p className="text-black/70 text-lg mb-8">
              Thank you! Your transportation request has been submitted. Shabi will review your request and contact you shortly with a quote.
            </p>
            <div className="bg-gray-50 p-6 border border-black/10 mb-8">
              <p className="text-sm text-black/60 uppercase tracking-wide mb-2">Need to speak with us now?</p>
              <a href={BRAND.phoneHref} className="text-2xl font-black text-[#E31E24] hover:text-[#C01A1F]">
                {BRAND.phone}
              </a>
            </div>
            <div className="flex gap-4 justify-center">
              <button onClick={() => window.location.href = "/"} className="border border-black/20 font-bold uppercase px-6 py-3 hover:bg-black hover:text-white transition-all">
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-black text-white pt-32 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-4">Get Started</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mb-2">
            Request a Quote / Book a Service
          </h1>
          <p className="text-white/80">Fast. Easy. No Hassle.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-10">
            {[
              { num: 1, label: "Contact Info" },
              { num: 2, label: "Trip Details" },
              { num: 3, label: "Vehicle Info" },
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black ${step >= s.num ? "bg-[#E31E24] text-white" : "bg-gray-200 text-black/40"}`}>
                  {s.num}
                </div>
                <div className="ml-3 flex-1">
                  <p className={`text-xs uppercase tracking-wide ${step >= s.num ? "text-black" : "text-black/40"}`}>Step {s.num}</p>
                  <p className={`text-sm font-bold ${step >= s.num ? "text-black" : "text-black/40"}`}>{s.label}</p>
                </div>
                {idx < 2 && <div className={`h-0.5 flex-1 mx-2 ${step > s.num ? "bg-[#E31E24]" : "bg-gray-200"}`}></div>}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 border border-black/10">
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className="text-2xl font-black uppercase mb-6">1. Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">Full Name *</label>
                    <input type="text" name="full_name" value={form.full_name} onChange={handleChange} required
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="John Doe" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold mb-2">Phone Number *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} required
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="(555) 123-4567" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2">Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="john@example.com" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className="text-2xl font-black uppercase mb-6">2. Trip Details</h2>
                <p className="text-black/60 text-sm mb-6">Fill us about your vehicle and transport details.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2 flex items-center gap-2"><MapPin size={16} />Pickup Address *</label>
                    <input type="text" name="pickup_address" value={form.pickup_address} onChange={handleChange} required
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="Enter pickup address" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 flex items-center gap-2"><MapPin size={16} />Delivery Address *</label>
                    <input type="text" name="delivery_address" value={form.delivery_address} onChange={handleChange} required
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="Enter delivery address" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold mb-2 flex items-center gap-2"><Calendar size={16} />Preferred Pickup Date</label>
                      <input type="date" name="pickup_date" value={form.pickup_date} onChange={handleChange}
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2 flex items-center gap-2"><Clock size={16} />Preferred Pickup Time</label>
                      <input type="time" name="pickup_time" value={form.pickup_time} onChange={handleChange}
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className="text-2xl font-black uppercase mb-6">3. Vehicle Information</h2>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold mb-2">Year</label>
                      <input type="text" name="vehicle_year" value={form.vehicle_year} onChange={handleChange}
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="2020" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2">Make *</label>
                      <input type="text" name="vehicle_make" value={form.vehicle_make} onChange={handleChange} required
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="Honda" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2">Model *</label>
                      <input type="text" name="vehicle_model" value={form.vehicle_model} onChange={handleChange} required
                        className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" placeholder="Civic" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Vehicle Type</label>
                    <select name="vehicle_type" value={form.vehicle_type} onChange={handleChange}
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none bg-white">
                      <option value="">Select type</option>
                      <option value="Sedan">Sedan</option>
                      <option value="SUV">SUV</option>
                      <option value="Truck">Truck</option>
                      <option value="Van">Van</option>
                      <option value="Motorcycle">Motorcycle</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="flex items-start gap-3 p-4 border border-black/20 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" name="runs_drives" checked={form.runs_drives} onChange={handleChange}
                        className="mt-1 w-5 h-5 accent-[#E31E24]" />
                      <div>
                        <p className="font-bold">Runs and Drives</p>
                        <p className="text-xs text-black/60">Vehicle starts and can be driven</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-4 border border-black/20 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" name="rolls_steers_brakes" checked={form.rolls_steers_brakes} onChange={handleChange}
                        className="mt-1 w-5 h-5 accent-[#E31E24]" />
                      <div>
                        <p className="font-bold">Rolls, Steers, Brakes</p>
                        <p className="text-xs text-black/60">Vehicle can be rolled onto trailer</p>
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Special Loading Requirements</label>
                    <textarea name="special_requirements" value={form.special_requirements} onChange={handleChange} rows={2}
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" placeholder="e.g., low ground clearance, lifted vehicle..." />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Additional Notes (Optional)</label>
                    <textarea name="additional_notes" value={form.additional_notes} onChange={handleChange} rows={3}
                      className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" placeholder="Any other details..." />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-black/10">
              {step > 1 ? (
                <button type="button" onClick={prevStep} className="border border-black/20 font-bold uppercase px-6 py-3 hover:bg-black hover:text-white transition-all">
                  Back
                </button>
              ) : (
                <div></div>
              )}
              {step < 3 ? (
                <button type="button" onClick={nextStep} className="bg-[#E31E24] text-white font-bold uppercase px-6 py-3 hover:bg-[#C01A1F] transition-all flex items-center gap-2">
                  Continue <ArrowRight size={18} />
                </button>
              ) : (
                <button type="submit" disabled={submitting} className="bg-[#E31E24] text-white font-bold uppercase px-6 py-3 hover:bg-[#C01A1F] transition-all flex items-center gap-2 disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Request"}
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-black/60 text-sm mb-2">Prefer to speak with us directly?</p>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 text-black font-bold hover:text-[#E31E24]">
              <Phone size={18} /> Call {BRAND.owner} at {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GetQuotePage;
